import { useCallback, useEffect, useRef, useState } from 'react';
import { GameFrame, PromptLine, pick, shuffle, splitFace, useGame, useRoundTimer, type GameProps } from '../sdk/react';
import { ODD_ONE_OUT } from '../phrases';
import { KINDS } from '../../concepts/kinds';
import { Pic } from '../sdk/react';

type Item = { say: string; answer: string };
const ASK = ODD_ONE_OUT;

export default function OddOneOut(props: GameProps) {
  const game = useGame(props);
  const items = ((game.config?.items ?? []) as Item[]).filter((i) => i?.say && i.answer && i.say.split(',').filter((s) => s.trim()).length >= 2);
  const [board, setBoard] = useState<string[]>([]);
  const [item, setItem] = useState<Item | null>(null);
  const [trial, setTrial] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [wrong, setWrong] = useState<number | null>(null);
  const queue = useRef<Item[]>([]);
  const busy = useRef(false);

  const start = useCallback(async () => {
    if (items.length === 0) return;
    if (queue.current.length === 0) queue.current = shuffle(items);
    const next = queue.current.shift() ?? pick(items);
    const tiles = shuffle([...next.say.split(',').map((s) => s.trim()).filter(Boolean), next.answer.trim()]);
    setDone(false);
    const id = await game.register({ tiles }, { value: next.answer.trim() });
    if (!id) return;
    setItem(next);
    setBoard(tiles);
    setTrial(id);
  }, [items.map((i) => i.say + i.answer).join('|')]);

  useEffect(() => { if (game.config) void start(); }, [game.config, start]);
  useEffect(() => {
    if (!item || game.textPrompt) return;
    const t = window.setTimeout(() => game.say(ASK), 300);
    return () => window.clearTimeout(t);
  }, [item, trial]);

  async function tap(i: number) {
    if (!item || !trial || done || busy.current) return;
    const face = board[i];
    const right = face === item.answer.trim();
    if (game.errorless && !right) return;
    busy.current = true;
    const v = await game.respond(trial, face);
    if (v?.correct) { setDone(true); busy.current = false; return; }
    setWrong(i);
    window.setTimeout(() => setWrong(null), 500);
    game.say(ASK);
    const fresh = await game.register({ tiles: board, retry: true }, { value: item.answer.trim() });
    busy.current = false;
    if (fresh) setTrial(fresh);
  }

  const fading = useRoundTimer(done, game.transitionMs, () => { void start(); });

  return (
    <GameFrame game={game} empty={items.length === 0} emptyText={`${KINDS.oddOneOut.describes} Edit it to add groups.`} onResume={() => { void start(); }} breakTrial={trial}>
      {item && (
        <div className={`g-board${fading ? ' is-fading' : ''}`}>
          <PromptLine game={game} text={ASK} />
          <div className={`tile-grid ${board.length <= 4 ? 'cols-2' : 'cols-3'}`} role="group" aria-label="Pictures">
            {board.map((t, i) => {
              const f = splitFace(t);
              const isAnswer = t === item.answer.trim();
              return (
                <button
                  key={`${t}-${i}`}
                  className={`tile g-face${done && isAnswer ? ' correct' : ''}${wrong === i ? ' is-bounce' : ''}`}
                  onClick={() => void tap(i)}
                  disabled={done}
                  aria-label={f.word || t}
                >
                  <Pic text={t} fallback={f.picture ? <span className="g-tile-pic" aria-hidden="true">{f.picture}</span> : null} className="g-tile-img" />
                  {f.word && <span className="g-tile-word">{f.word}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </GameFrame>
  );
}
